import React, { useState } from 'react';
import { EventDtoV2 } from 'src/api/v2';
import QRCodeCanvas from 'qrcode.react';
import css from './EventSidebar.module.scss';
import { useTranslation } from 'react-i18next';
import AddEntityModalButton from 'src/components/AddEntityModalButton/AddEntityModalButton';
import QrCodePrintModal from 'src/features/projects/details/components/QrCodePrintModal';
import { formatAsCurrencyString } from 'src/utils/FormatValue';
import useEventDetails from 'src/hooks/use-event-details';
import { useBoolean } from 'usehooks-ts';
import { RegisterInkindModal } from 'src/features/inkind';
import { Button, Heading, Icon, Textfield } from '@innovationnorway/cl-react';

type EventSidebarProps = {
  event: EventDtoV2;
};
const EventSidebar: React.FC<EventSidebarProps> = ({ event }) => {
  const { t: tCommon } = useTranslation();
  const { t: tEvents } = useTranslation('events');
  const { t: tSelfEffort } = useTranslation('selfEffort');
  const { eventDetails, refetch: refetchDetails } = useEventDetails(event.id);
  const registerInKindModal = useBoolean();
  const [inviteLinkText, setInviteLinkText] = useState<string>(tEvents('copyRegistrationLink'));
  const inviteLink = `${window.location.origin}/event-registration/${event.id}`;
  const inviteLinkClickHandler = () => {
    navigator.clipboard.writeText(inviteLink);
    setInviteLinkText(tEvents('linkCopied'));
    setTimeout(() => {
      setInviteLinkText(tEvents('copyRegistrationLink'));
    }, 2000);
  };
  function inKindOnCloseHandler() {
    registerInKindModal.setFalse();
    refetchDetails();
  }
  return (
    <div className={css['event-sidebar']}>
      <Button type="button" onClick={registerInKindModal.setTrue}>
        <Icon prefix="fasr" iconName="sack-dollar" />
        {tSelfEffort('registerSelfEffort')}
      </Button>
      <Heading level={2}>{tEvents('selfRegistration')}</Heading>
      <div className={css['event-sidebar__qr-code']}>
        <p>{tEvents('qrCodeSidebarDescription')}</p>
        <QRCodeCanvas value={inviteLink} size={200} />
        <div className={css['event-sidebar__qr-code--button']}>
          <AddEntityModalButton
            buttonText={tEvents('printQrCode')}
            modalTitle={`${tEvents('qrCodeFor')} ${event.name}`}
            buttonVariant="secondary"
          >
            <QrCodePrintModal event={event} inviteLink={inviteLink} />
          </AddEntityModalButton>
        </div>
      </div>
      <div className="stack margin-top--2">
        <Textfield label={tEvents('registrationLink')} value={inviteLink} readOnly />
        <Button type="button" onClick={inviteLinkClickHandler} variant="secondary">
          {inviteLinkText}
        </Button>
      </div>

      <div className={css['event-sidebar__results']}>
        <Heading level={2}>{tCommon('result')}</Heading>
        <p>
          <Icon prefix="fasr" iconName="user" size="xs" className={css['event-sidebar__results--icon']} />
          {eventDetails?.participants} {tCommon('participants').toLowerCase()}
        </p>
        <p>
          <Icon
            prefix="fasr"
            iconName="calendar-edit"
            size="xs"
            className={css['event-sidebar__results--icon']}
          />
          {eventDetails?.timeEntryHours} {tSelfEffort('hoursRegistered')}
        </p>
        <p>
          <Icon
            prefix="fasr"
            iconName="sack-dollar"
            size="xs"
            className={css['event-sidebar__results--icon']}
          />
          {formatAsCurrencyString(
            eventDetails?.inKindTotal ? eventDetails.inKindTotal : 0,
            ' ',
            undefined,
            0,
            undefined,
            ',-',
          )}{' '}
          {tCommon('inkind').toLowerCase()}
        </p>
        <RegisterInkindModal
          show={registerInKindModal.value}
          onClose={inKindOnCloseHandler}
          defaultValues={{ projectId: event.projectId, eventId: event.id }}
        />
      </div>
    </div>
  );
};
export default EventSidebar;
